import {
  getObject,
  getOptionalObject,
  getInt32,
  getFloat,
} from "@surya-digital/leo-ts-types";
import { RespondentColumn, RespondentColumnDTO } from "../types/respondentColumn";
import { RespondentCount, RespondentCountDTO } from "./respondentCount";

export interface PivotTableRowDTO {
  distinctColumnValue?: RespondentColumnDTO | null;
  total: number;
  responseRate: number;
  notAssignedCount: RespondentCountDTO;
  notStartedCount: RespondentCountDTO;
  viewedCount: RespondentCountDTO;
  startedCount: RespondentCountDTO;
  inProgressCount: RespondentCountDTO;
  lastPageCount: RespondentCountDTO;
  submittedCount: RespondentCountDTO;
}

export class PivotTableRow {
  readonly isPivotTableRowPivotTableRow: boolean = true;

  readonly distinctColumnValue: RespondentColumn | null;
  readonly total: number;
  readonly responseRate: number;
  readonly notAssignedCount: RespondentCount;
  readonly notStartedCount: RespondentCount;
  readonly viewedCount: RespondentCount;
  readonly startedCount: RespondentCount;
  readonly inProgressCount: RespondentCount;
  readonly lastPageCount: RespondentCount;
  readonly submittedCount: RespondentCount;

  static readonly constraints = {
    "responseRate": {
      "minValue": 0,
      "maxValue": 100,
    },
  }

  constructor(
  distinctColumnValue: RespondentColumn | null = null, 
  total: number, 
  responseRate: number, 
  notAssignedCount: RespondentCount, 
  notStartedCount: RespondentCount, 
  viewedCount: RespondentCount, 
  startedCount: RespondentCount, 
  inProgressCount: RespondentCount, 
  lastPageCount: RespondentCount, 
  submittedCount: RespondentCount
  ) {
    this.distinctColumnValue = distinctColumnValue;
    this.total = total;
    PivotTableRow.validateResponseRate(responseRate);
    this.responseRate = responseRate;
    this.notAssignedCount = notAssignedCount;
    this.notStartedCount = notStartedCount;
    this.viewedCount = viewedCount;
    this.startedCount = startedCount;
    this.inProgressCount = inProgressCount;
    this.lastPageCount = lastPageCount;
    this.submittedCount = submittedCount;
  }

  static fromDTO(dto: PivotTableRowDTO): PivotTableRow {
    let distinctColumnValue = getOptionalObject(dto, "distinctColumnValue");
    if(distinctColumnValue !== undefined && distinctColumnValue !== null) {
      distinctColumnValue = RespondentColumn.fromDTO(getObject(dto, "distinctColumnValue", InvalidPivotTableRowDistinctColumnValueError));
    }
    const total = getInt32(dto, "total", InvalidPivotTableRowTotalError);
    const responseRate = getFloat(dto, "responseRate", InvalidPivotTableRowResponseRateError);
    const notAssignedCount = RespondentCount.fromDTO(getObject(dto, "notAssignedCount", InvalidPivotTableRowNotAssignedCountError));
    const notStartedCount = RespondentCount.fromDTO(getObject(dto, "notStartedCount", InvalidPivotTableRowNotStartedCountError));
    const viewedCount = RespondentCount.fromDTO(getObject(dto, "viewedCount", InvalidPivotTableRowViewedCountError));
    const startedCount = RespondentCount.fromDTO(getObject(dto, "startedCount", InvalidPivotTableRowStartedCountError));
    const inProgressCount = RespondentCount.fromDTO(getObject(dto, "inProgressCount", InvalidPivotTableRowInProgressCountError));
    const lastPageCount = RespondentCount.fromDTO(getObject(dto, "lastPageCount", InvalidPivotTableRowLastPageCountError));
    const submittedCount = RespondentCount.fromDTO(getObject(dto, "submittedCount", InvalidPivotTableRowSubmittedCountError));
    return new PivotTableRow(
      distinctColumnValue,
      total,
      responseRate,
      notAssignedCount,
      notStartedCount,
      viewedCount,
      startedCount,
      inProgressCount,
      lastPageCount,
      submittedCount
    );
  }

  toDTO(): PivotTableRowDTO {
    const dto: PivotTableRowDTO = {
        total: this.total,
        responseRate: this.responseRate,
        notAssignedCount: this.notAssignedCount.toDTO(),
        notStartedCount: this.notStartedCount.toDTO(),
        viewedCount: this.viewedCount.toDTO(),
        startedCount: this.startedCount.toDTO(),
        inProgressCount: this.inProgressCount.toDTO(),
        lastPageCount: this.lastPageCount.toDTO(),
        submittedCount: this.submittedCount.toDTO()
    };
    if (this.distinctColumnValue !== undefined && this.distinctColumnValue !== null){
      dto.distinctColumnValue = this.distinctColumnValue.toDTO();
    }
    return dto;
  }
  private static validateResponseRate = (responseRate: number): void => {
    if(responseRate < 0) {
      throw new InvalidPivotTableRowResponseRateMinValueError(
        0,
        `Attribute responseRate has value ${responseRate}. Min value is 0.`
      );
    }
    if(responseRate > 100) {
      throw new InvalidPivotTableRowResponseRateMaxValueError(
        100,
        `Attribute responseRate has value ${responseRate}. Max value is 100.`
      );
    }
  }
  
}

PivotTableRow.prototype.toString = function toString(): string {
  return `PivotTableRow(distinctColumnValue='${this.distinctColumnValue}',total='${this.total}',responseRate='${this.responseRate}',notAssignedCount='${this.notAssignedCount}',notStartedCount='${this.notStartedCount}',viewedCount='${this.viewedCount}',startedCount='${this.startedCount}',inProgressCount='${this.inProgressCount}',lastPageCount='${this.lastPageCount}',submittedCount='${this.submittedCount}')`;
};

export class InvalidPivotTableRowError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowError";
  }
}
export class InvalidPivotTableRowDistinctColumnValueError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowDistinctColumnValueError";
  }
}

export class InvalidPivotTableRowTotalError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowTotalError";
  }
}

export class InvalidPivotTableRowResponseRateError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowResponseRateError";
  }
}

export class InvalidPivotTableRowResponseRateMaxValueError extends InvalidPivotTableRowResponseRateError {
  readonly maxValue: number;
  constructor(maxValue: number, message?: string) {
    super(message);
    this.maxValue = maxValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowResponseRateMaxValueError";
  }
}

export class InvalidPivotTableRowResponseRateMinValueError extends InvalidPivotTableRowResponseRateError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowResponseRateMinValueError";
  }
}

export class InvalidPivotTableRowNotAssignedCountError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowNotAssignedCountError";
  }
}

export class InvalidPivotTableRowNotStartedCountError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowNotStartedCountError";
  }
}

export class InvalidPivotTableRowViewedCountError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowViewedCountError";
  }
}

export class InvalidPivotTableRowStartedCountError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowStartedCountError";
  }
}

export class InvalidPivotTableRowInProgressCountError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowInProgressCountError";
  }
}

export class InvalidPivotTableRowLastPageCountError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowLastPageCountError";
  }
}

export class InvalidPivotTableRowSubmittedCountError extends InvalidPivotTableRowError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidPivotTableRowSubmittedCountError";
  }
}
